// src/components/emision/EmisionDTEPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, TextField, Button,
  Select, MenuItem, FormControl, InputLabel, Alert,
  CircularProgress, Chip, Grid, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
  Switch, FormControlLabel, Collapse,
} from '@mui/material';
import {
  DescriptionOutlined, SendOutlined, SearchOutlined,
  FolderOutlined, AddCircleOutline, DeleteOutline,
  DownloadOutlined, ContentCopy, CheckCircleOutlined,
  RefreshOutlined, ListAltOutlined, ExpandMoreOutlined,
  LocalShippingOutlined, CloudSyncOutlined,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useAuth } from '../auth/AuthContext';
import { emisionDTEService, TIPOS_DTE } from '../../services/emisionDTEService';
import { empresaService } from '../../services/empresaService';
import FoliosPanel from './FoliosPanel';

// ── Helpers ───────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? '¡Copiado!' : 'Copiar'}>
      <IconButton size="small" onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}>
        {copied ? <CheckCircleOutlined fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

function XmlDisplay({ xml, label }) {
  if (!xml) return null;
  const text = typeof xml === 'string' ? xml : JSON.stringify(xml, null, 2);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <CopyButton text={text} />
        <Button size="small" startIcon={<DownloadOutlined />} onClick={() => {
          const blob = new Blob([text], { type: 'text/xml' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url;
          a.download = `${label.replace(/\s+/g, '_')}.xml`; a.click();
          URL.revokeObjectURL(url);
        }}>Descargar</Button>
      </Box>
      <Box component="pre" sx={{
        background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 1,
        p: 2, fontSize: '0.72rem', overflowX: 'auto', maxHeight: 360,
        whiteSpace: 'pre-wrap', wordBreak: 'break-all',
      }}>
        {text}
      </Box>
    </Box>
  );
}

const ESTADO_INFO = {
  ACEPTADO:   { label: 'Aceptado',           color: 'success' },
  REPARO:     { label: 'Con reparo',          color: 'warning' },
  RECHAZADO:  { label: 'Rechazado',           color: 'error'   },
  PENDIENTE:  { label: 'Pendiente',           color: 'default' },
  GENERADO:   { label: 'Solo generado',       color: 'info'    },
  DOK:        { label: 'Aceptado',            color: 'success' },
  DOK1:       { label: 'Aceptado c/reparo',   color: 'warning' },
  DNK:        { label: 'No encontrado',       color: 'default' },
  FAU:        { label: 'Firma no válida',     color: 'error'   },
  FNA:        { label: 'No autorizado',       color: 'error'   },
  ERR:        { label: 'Error',               color: 'error'   },
};

function EstadoChip({ estado }) {
  const info = ESTADO_INFO[estado] || { label: estado || '–', color: 'default' };
  return <Chip label={info.label} color={info.color} size="small" />;
}

// ── Tab 1: Folios / CAF ───────────────────────────────────────────────────────

function CafProgressBar({ usado, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((usado / total) * 100)) : 0;
  const color = pct >= 90 ? '#d32f2f' : pct >= 70 ? '#f57c00' : '#2e7d32';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, bgcolor: '#e0e0e0', borderRadius: 1, height: 8, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, bgcolor: color, height: '100%', borderRadius: 1, transition: 'width 0.3s' }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {usado}/{total} ({pct}%)
      </Typography>
    </Box>
  );
}

function TabFolios({ rutEmpresa }) {
  const [cafs, setCafs] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ambiente, setAmbiente] = useState('certificacion');
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const cargarCafs = useCallback(async () => {
    if (!rutEmpresa) return;
    setLoadingList(true);
    try { setCafs(await emisionDTEService.listarCafs(rutEmpresa)); }
    catch (e) { setError(e.message); }
    finally { setLoadingList(false); }
  }, [rutEmpresa]);

  useEffect(() => { cargarCafs(); }, [cargarCafs]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError(null); setMsg(null);
    try {
      const res = await emisionDTEService.uploadCaf(rutEmpresa, file, ambiente);
      setMsg(`CAF cargado: Tipo ${res.tipo_dte}, folios ${res.folio_desde}–${res.folio_hasta}, vence ${res.fecha_vencimiento}`);
      cargarCafs();
    } catch (e) { setError(e.message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const cafsFiltrados = cafs.filter(c => c.ambiente === ambiente);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">Folios / CAF</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Ambiente CAF</InputLabel>
            <Select value={ambiente} label="Ambiente CAF" onChange={e => setAmbiente(e.target.value)}>
              <MenuItem value="certificacion">Certificación</MenuItem>
              <MenuItem value="produccion">Producción</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained" component="label"
            startIcon={uploading ? <CircularProgress size={16} /> : <FolderOutlined />}
            disabled={uploading}
          >
            {uploading ? 'Subiendo...' : 'Subir CAF (.xml)'}
            <input type="file" accept=".xml" hidden onChange={handleUpload} />
          </Button>
          <Button
            variant="outlined"
            startIcon={<SearchOutlined />}
            onClick={() => setPanelOpen(true)}
            disabled={cafsFiltrados.length === 0}
          >
            Ver folios
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Descargue el CAF desde <strong>maullin.sii.cl</strong> (certificación) o <strong>sii.cl</strong> (producción)
        y súbalo aquí. El sistema asigna folios automáticamente al emitir DTEs.
      </Alert>

      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loadingList ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : cafsFiltrados.length === 0 ? (
        <Alert severity="warning">No hay CAFs cargados para ambiente <strong>{ambiente}</strong>.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tipo DTE</TableCell>
              <TableCell align="center">Folios</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Uso</TableCell>
              <TableCell>Próximo folio</TableCell>
              <TableCell>Vencimiento</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {cafsFiltrados.map(caf => {
              const vencido = caf.fecha_vencimiento && new Date(caf.fecha_vencimiento) < new Date();
              return (
                <TableRow key={caf.id} sx={{ opacity: caf.activo ? 1 : 0.5 }}>
                  <TableCell>
                    <Chip label={`T${caf.tipo_dte}`} size="small" color="primary" variant="outlined" />
                    <Typography variant="caption" sx={{ ml: 1 }}>
                      {TIPOS_DTE.find(t => t.codigo === caf.tipo_dte)?.nombre || ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption">{caf.folio_desde}–{caf.folio_hasta}</Typography>
                  </TableCell>
                  <TableCell>
                    <CafProgressBar usado={caf.folios_usados} total={caf.folios_total} />
                    <Typography variant="caption" color="text.secondary">
                      {caf.folios_disponibles} disponibles
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{caf.folio_actual}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color={vencido ? 'error' : 'inherit'}>
                      {caf.fecha_vencimiento || '–'}{vencido ? ' ⚠' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {!caf.activo ? <Chip label="Inactivo" size="small" />
                      : caf.agotado ? <Chip label="Agotado" size="small" color="error" />
                      : vencido ? <Chip label="Vencido" size="small" color="warning" />
                      : <Chip label="Activo" size="small" color="success" />}
                  </TableCell>
                  <TableCell>
                    {caf.activo && (
                      <Tooltip title="Desactivar">
                        <IconButton size="small" color="error" onClick={async () => {
                          try { await emisionDTEService.desactivarCaf(caf.id); cargarCafs(); }
                          catch (e) { setError(e.message); }
                        }}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <FoliosPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        rutEmpresa={rutEmpresa}
        cafsTipos={cafsFiltrados}
      />
    </Box>
  );
}

// ── Tab 2: Emitir DTE ─────────────────────────────────────────────────────────

const ITEM_VACIO = { nombre: '', descripcion: '', unidad: '', cantidad: 1, precio: 0, monto_item: 0, exento: false, descuento: null };

const REF_VACIA = { tipo_doc: 'SET', folio: '', fecha: new Date().toISOString().slice(0, 10), cod_ref: '', razon: '' };
const DCTO_VACIO = { tipo_movimiento: 'D', glosa: 'Descuento Global', tipo_valor: '%', valor: '' };

const COD_REF_OPTS = [
  { value: '',  label: 'Sin código' },
  { value: '1', label: '1 – Anula documento' },
  { value: '2', label: '2 – Corrige textos' },
  { value: '3', label: '3 – Corrige montos' },
];

const TIPOS_DOC_REF = [
  { value: 'SET', label: 'SET (pruebas SII)' },
  { value: '33',  label: '33 – Factura' },
  { value: '34',  label: '34 – Factura Exenta' },
  { value: '52',  label: '52 – Guía de Despacho' },
  { value: '56',  label: '56 – Nota de Débito' },
  { value: '61',  label: '61 – Nota de Crédito' },
  { value: '46',  label: '46 – Factura de Compra' },
  { value: '39',  label: '39 – Boleta' },
];

const IND_TRASLADO_OPTS = [
  { value: 1, label: '1 – Operación constituye venta' },
  { value: 2, label: '2 – Ventas por efectuar' },
  { value: 3, label: '3 – Consignaciones' },
  { value: 4, label: '4 – Entrega gratuita' },
  { value: 5, label: '5 – Traslados internos' },
  { value: 6, label: '6 – Otros traslados no venta' },
  { value: 7, label: '7 – Guía de devolución' },
  { value: 8, label: '8 – Traslado para exportación' },
  { value: 9, label: '9 – Ventas para exportación' },
];

function TabEmitir({ rutEmpresa, empresaActiva }) {
  const [tipoDTE, setTipoDTE] = useState(33);
  const [fechaEmision, setFechaEmision] = useState(new Date().toISOString().slice(0, 10));
  const [soloGenerar, setSoloGenerar] = useState(false);

  // Emisor
  const [emisor, setEmisor] = useState({
    rut: empresaActiva ? `${empresaActiva.rut}-${empresaActiva.dv}` : '',
    razon_social: empresaActiva?.razon_social || '',
    giro: '',
    acteco: 620200,
    direccion: '',
    comuna: 'Santiago',
    ciudad: 'Santiago',
  });

  // Receptor
  const [receptor, setReceptor] = useState({
    rut: '', razon_social: '', giro: '', direccion: '', comuna: 'Santiago', ciudad: 'Santiago',
  });

  // Configuración envío
  const [rutEnvia, setRutEnvia] = useState('');
  const [fechaResolucion, setFechaResolucion] = useState('2026-04-16');
  const [nroResolucion, setNroResolucion] = useState(0);

  // Guía de Despacho (tipo 52)
  const [transporte, setTransporte] = useState({
    ind_traslado: 1, tipo_despacho: null,
    rut_chofer: '', nombre_chofer: '', patente: '',
    dir_dest: '', cmna_dest: '', ciudad_dest: '',
  });
  const setT = (k, v) => setTransporte(p => ({ ...p, [k]: v }));

  const [items, setItems] = useState([{ ...ITEM_VACIO }]);
  const [referencias, setReferencias] = useState([{ ...REF_VACIA }]);
  const [descuentosGlobales, setDescuentosGlobales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!rutEmpresa) return;
    empresaService.getConfigTributaria(rutEmpresa)
      .then(cfg => {
        if (cfg?.existe) {
          setEmisor(prev => ({
            ...prev,
            giro: cfg.giro || prev.giro,
            acteco: cfg.acteco || prev.acteco,
            direccion: cfg.direccion || prev.direccion,
            comuna: cfg.comuna || prev.comuna,
            ciudad: cfg.ciudad || prev.ciudad,
          }));
        }
      })
      .catch(() => {});
  }, [rutEmpresa]);

  const setE = (k, v) => setEmisor(p => ({ ...p, [k]: v }));
  const setR = (k, v) => setReceptor(p => ({ ...p, [k]: v }));

  const updateItem = (idx, field, val) => {
    setItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      if (field === 'cantidad' || field === 'precio' || field === 'descuento') {
        const qty = field === 'cantidad' ? parseFloat(val) || 0 : parseFloat(next[idx].cantidad) || 0;
        const prc = field === 'precio' ? parseFloat(val) || 0 : parseFloat(next[idx].precio) || 0;
        const desc = field === 'descuento' ? parseFloat(val) || 0 : parseFloat(next[idx].descuento) || 0;
        next[idx].monto_item = Math.round(qty * prc * (1 - desc / 100));
      }
      return next;
    });
  };

  const totalNeto = items.filter(it => !it.exento).reduce((s, it) => s + (parseInt(it.monto_item) || 0), 0);
  const totalExento = items.filter(it => it.exento).reduce((s, it) => s + (parseInt(it.monto_item) || 0), 0);
  const tasa = tipoDTE === 34 ? 0 : 19;
  const iva = Math.round(totalNeto * tasa / 100);
  const total = totalNeto + iva + totalExento;

  const handleEmitir = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const res = await emisionDTEService.emitirDTE({
        rut_empresa: rutEmpresa,
        rut_envia: rutEnvia,
        tipo_dte: tipoDTE,
        fecha_emision: fechaEmision,
        fecha_resolucion: fechaResolucion,
        nro_resolucion: parseInt(nroResolucion),
        emisor,
        receptor,
        items: items.map((it, i) => ({
          nro_linea: i + 1,
          nombre: it.nombre,
          descripcion: it.descripcion || null,
          unidad: it.unidad || null,
          cantidad: parseFloat(it.cantidad) || 1,
          precio: parseInt(it.precio) || 0,
          monto_item: parseInt(it.monto_item) || 0,
          exento: it.exento,
          descuento: it.descuento ? parseFloat(it.descuento) : null,
        })),
        referencias: referencias
          .filter(r => r.folio && r.razon)
          .map(r => ({
            tipo_doc: r.tipo_doc,
            folio: r.folio,
            fecha: r.fecha || fechaEmision,
            cod_ref: r.cod_ref || null,
            razon: r.razon,
          })),
        descuentos_globales: descuentosGlobales
          .filter(d => d.valor !== '' && parseFloat(d.valor) > 0)
          .map(d => ({
            tipo_movimiento: d.tipo_movimiento,
            glosa: d.glosa,
            tipo_valor: d.tipo_valor,
            valor: parseFloat(d.valor),
          })),
        transporte: tipoDTE === 52 ? {
          ind_traslado: parseInt(transporte.ind_traslado) || 1,
          tipo_despacho: transporte.tipo_despacho ? parseInt(transporte.tipo_despacho) : null,
          rut_chofer: transporte.rut_chofer || null,
          nombre_chofer: transporte.nombre_chofer || null,
          patente: transporte.patente || null,
          dir_dest: transporte.dir_dest || null,
          cmna_dest: transporte.cmna_dest || null,
          ciudad_dest: transporte.ciudad_dest || null,
        } : null,
        solo_generar: soloGenerar,
      });
      setResult(res);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Emitir DTE</Typography>

      <Grid container spacing={2}>
        {/* Tipo + Fecha */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo DTE</InputLabel>
            <Select value={tipoDTE} label="Tipo DTE" onChange={e => setTipoDTE(e.target.value)}>
              {TIPOS_DTE.map(t => <MenuItem key={t.codigo} value={t.codigo}>{t.codigo} – {t.nombre}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField fullWidth size="small" label="Fecha emisión" type="date"
            value={fechaEmision} onChange={e => setFechaEmision(e.target.value)}
            InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <FormControlLabel
            control={<Switch checked={soloGenerar} onChange={e => setSoloGenerar(e.target.checked)} />}
            label="Solo generar XML (no enviar)"
          />
        </Grid>

        {/* Emisor */}
        <Grid item xs={12}><Typography variant="subtitle2" color="primary">Emisor</Typography></Grid>
        <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="RUT" value={emisor.rut} onChange={e => setE('rut', e.target.value)} /></Grid>
        <Grid item xs={6} sm={5}><TextField fullWidth size="small" label="Razón Social" value={emisor.razon_social} onChange={e => setE('razon_social', e.target.value)} /></Grid>
        <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Giro" value={emisor.giro} onChange={e => setE('giro', e.target.value)} /></Grid>
        <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Acteco" value={emisor.acteco} onChange={e => setE('acteco', parseInt(e.target.value) || 0)} /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Dirección" value={emisor.direccion} onChange={e => setE('direccion', e.target.value)} /></Grid>
        <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Comuna" value={emisor.comuna} onChange={e => setE('comuna', e.target.value)} /></Grid>
        <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Ciudad" value={emisor.ciudad} onChange={e => setE('ciudad', e.target.value)} /></Grid>

        {/* Receptor */}
        <Grid item xs={12}><Typography variant="subtitle2" color="primary">Receptor</Typography></Grid>
        <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="RUT" value={receptor.rut} onChange={e => setR('rut', e.target.value)} /></Grid>
        <Grid item xs={6} sm={5}><TextField fullWidth size="small" label="Razón Social" value={receptor.razon_social} onChange={e => setR('razon_social', e.target.value)} /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Giro" value={receptor.giro} onChange={e => setR('giro', e.target.value)} /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Dirección" value={receptor.direccion} onChange={e => setR('direccion', e.target.value)} /></Grid>
        <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Comuna" value={receptor.comuna} onChange={e => setR('comuna', e.target.value)} /></Grid>
        <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Ciudad" value={receptor.ciudad} onChange={e => setR('ciudad', e.target.value)} /></Grid>

        {/* Datos transporte — solo Guía de Despacho (tipo 52) */}
        {tipoDTE === 52 && (
          <>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShippingOutlined color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="primary">Transporte (Guía de Despacho)</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Indicador Traslado</InputLabel>
                <Select value={transporte.ind_traslado} label="Indicador Traslado"
                  onChange={e => setT('ind_traslado', e.target.value)}>
                  {IND_TRASLADO_OPTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Despacho</InputLabel>
                <Select value={transporte.tipo_despacho || ''} label="Tipo Despacho"
                  onChange={e => setT('tipo_despacho', e.target.value || null)}>
                  <MenuItem value="">Sin especificar</MenuItem>
                  <MenuItem value={1}>1 – Despacho directo</MenuItem>
                  <MenuItem value={2}>2 – Envío a domicilio</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Patente" value={transporte.patente} onChange={e => setT('patente', e.target.value)} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="RUT Chofer" value={transporte.rut_chofer} onChange={e => setT('rut_chofer', e.target.value)} /></Grid>
            <Grid item xs={6} sm={5}><TextField fullWidth size="small" label="Nombre Chofer" value={transporte.nombre_chofer} onChange={e => setT('nombre_chofer', e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Dirección destino" value={transporte.dir_dest} onChange={e => setT('dir_dest', e.target.value)} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Comuna destino" value={transporte.cmna_dest} onChange={e => setT('cmna_dest', e.target.value)} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Ciudad destino" value={transporte.ciudad_dest} onChange={e => setT('ciudad_dest', e.target.value)} /></Grid>
          </>
        )}

        {/* Configuración envío */}
        <Grid item xs={12}><Typography variant="subtitle2" color="primary">Configuración de envío</Typography></Grid>
        <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="RUT quien envía (firmante)" value={rutEnvia} onChange={e => setRutEnvia(e.target.value)} placeholder="12345678-9" /></Grid>
        <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Fecha resolución SII" type="date" value={fechaResolucion} onChange={e => setFechaResolucion(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
        <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Nro. resolución" type="number" value={nroResolucion} onChange={e => setNroResolucion(e.target.value)} /></Grid>

        {/* Ítems */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" color="primary">Líneas de detalle</Typography>
            <IconButton size="small" onClick={() => setItems(p => [...p, { ...ITEM_VACIO }])}>
              <AddCircleOutline fontSize="small" />
            </IconButton>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre ítem</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Unidad</TableCell>
                <TableCell align="right">Cant.</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">Desc%</TableCell>
                <TableCell align="center">Exento</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it, idx) => (
                <TableRow key={idx}>
                  <TableCell><TextField size="small" fullWidth value={it.nombre} onChange={e => updateItem(idx, 'nombre', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" fullWidth value={it.descripcion} onChange={e => updateItem(idx, 'descripcion', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" sx={{ width: 70 }} value={it.unidad} onChange={e => updateItem(idx, 'unidad', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" type="number" sx={{ width: 70 }} value={it.cantidad} onChange={e => updateItem(idx, 'cantidad', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" type="number" sx={{ width: 100 }} value={it.precio} onChange={e => updateItem(idx, 'precio', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" type="number" sx={{ width: 70 }} value={it.descuento || ''} onChange={e => updateItem(idx, 'descuento', e.target.value || null)} /></TableCell>
                  <TableCell align="center">
                    <Switch size="small" checked={it.exento} onChange={e => updateItem(idx, 'exento', e.target.checked)} />
                  </TableCell>
                  <TableCell align="right"><Typography variant="body2">{(it.monto_item || 0).toLocaleString('es-CL')}</Typography></TableCell>
                  <TableCell>
                    {items.length > 1 && (
                      <IconButton size="small" onClick={() => setItems(p => p.filter((_, i) => i !== idx))}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {[
                ['Neto', totalNeto],
                tasa ? ['IVA 19%', iva] : null,
                totalExento ? ['Exento', totalExento] : null,
                ['Total', total],
              ].filter(Boolean).map(([label, val]) => (
                <TableRow key={label}>
                  <TableCell colSpan={7} align="right"><Typography variant={label === 'Total' ? 'subtitle2' : 'caption'}>{label}:</Typography></TableCell>
                  <TableCell align="right"><Typography variant={label === 'Total' ? 'subtitle2' : 'body2'}>{val.toLocaleString('es-CL')}</Typography></TableCell>
                  <TableCell />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>

        {/* Referencias */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" color="primary">Referencias</Typography>
            <IconButton size="small" onClick={() => setReferencias(p => [...p, { ...REF_VACIA }])}>
              <AddCircleOutline fontSize="small" />
            </IconButton>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 160 }}>Tipo doc</TableCell>
                <TableCell sx={{ width: 90 }}>Folio</TableCell>
                <TableCell sx={{ width: 140 }}>Fecha</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Código referencia</TableCell>
                <TableCell>Razón referencia</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {referencias.map((ref, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select value={ref.tipo_doc} onChange={e => setReferencias(p => p.map((r, i) => i === idx ? { ...r, tipo_doc: e.target.value } : r))}>
                        {TIPOS_DOC_REF.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={ref.folio}
                      onChange={e => setReferencias(p => p.map((r, i) => i === idx ? { ...r, folio: e.target.value } : r))} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="date" fullWidth value={ref.fecha}
                      InputLabelProps={{ shrink: true }}
                      onChange={e => setReferencias(p => p.map((r, i) => i === idx ? { ...r, fecha: e.target.value } : r))} />
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select value={ref.cod_ref} onChange={e => setReferencias(p => p.map((r, i) => i === idx ? { ...r, cod_ref: e.target.value } : r))}>
                        {COD_REF_OPTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={ref.razon}
                      onChange={e => setReferencias(p => p.map((r, i) => i === idx ? { ...r, razon: e.target.value } : r))} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => setReferencias(p => p.filter((_, i) => i !== idx))}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {referencias.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: 'text.secondary', fontSize: '0.8rem', py: 1 }}>
                    Sin referencias (opcional)
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Grid>

        {/* Descuentos / Recargos globales */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" color="primary">Descuentos / Recargos globales</Typography>
            <IconButton size="small" onClick={() => setDescuentosGlobales(p => [...p, { ...DCTO_VACIO }])}>
              <AddCircleOutline fontSize="small" />
            </IconButton>
          </Box>
          {descuentosGlobales.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 130 }}>Tipo</TableCell>
                  <TableCell sx={{ width: 80 }}>Valor</TableCell>
                  <TableCell sx={{ width: 80 }}>Unidad</TableCell>
                  <TableCell>Glosa</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {descuentosGlobales.map((d, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select value={d.tipo_movimiento} onChange={e => setDescuentosGlobales(p => p.map((x, i) => i === idx ? { ...x, tipo_movimiento: e.target.value } : x))}>
                          <MenuItem value="D">Descuento</MenuItem>
                          <MenuItem value="R">Recargo</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" fullWidth value={d.valor}
                        onChange={e => setDescuentosGlobales(p => p.map((x, i) => i === idx ? { ...x, valor: e.target.value } : x))} />
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select value={d.tipo_valor} onChange={e => setDescuentosGlobales(p => p.map((x, i) => i === idx ? { ...x, tipo_valor: e.target.value } : x))}>
                          <MenuItem value="%">%</MenuItem>
                          <MenuItem value="$">$</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField size="small" fullWidth value={d.glosa}
                        onChange={e => setDescuentosGlobales(p => p.map((x, i) => i === idx ? { ...x, glosa: e.target.value } : x))} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => setDescuentosGlobales(p => p.filter((_, i) => i !== idx))}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained" color="primary" size="large"
            onClick={handleEmitir} disabled={loading}
            startIcon={loading ? <CircularProgress size={18} /> : soloGenerar ? <DescriptionOutlined /> : <SendOutlined />}
          >
            {loading ? 'Procesando...' : soloGenerar ? 'Generar DTE firmado' : 'Generar y enviar al SII'}
          </Button>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {result && (
        <Box sx={{ mt: 3 }}>
          <Alert severity={result.ok ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {result.ok
              ? `✓ ${soloGenerar ? 'DTE generado' : 'Enviado al SII'} — Folio ${result.folio_usado} · ${result.folios_restantes} folios restantes${result.trackid ? ` · TrackID: ${result.trackid}` : ''}`
              : `Folio ${result.folio_usado} generado. ${result.estado || ''}`
            }
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            {result.folio_usado && <Chip label={`Folio: ${result.folio_usado}`} color="primary" size="small" />}
            {result.trackid && <Chip label={`TrackID: ${result.trackid}`} color="success" size="small" />}
            {result.ambiente && <Chip label={result.ambiente} size="small" color={result.ambiente === 'produccion' ? 'error' : 'default'} />}
          </Box>
          <XmlDisplay xml={result.dte_xml} label="DTE firmado" />
          {result.envio_xml && <Box sx={{ mt: 2 }}><XmlDisplay xml={result.envio_xml} label="EnvioDTE" /></Box>}
        </Box>
      )}
    </Box>
  );
}

// ── Tab 3: Documentos Emitidos ────────────────────────────────────────────────

function TabDocumentos({ rutEmpresa }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [refreshingType, setRefreshingType] = useState(null); // 'envio' | 'dte'
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [envioResultById, setEnvioResultById] = useState({}); // { [dteId]: result }

  // Filtros
  const [ambiente, setAmbiente] = useState('certificacion');
  const [tipoDte, setTipoDte] = useState('');
  const [periodo, setPeriodo] = useState('');

  const cargar = useCallback(async () => {
    if (!rutEmpresa) return;
    setLoading(true); setError(null);
    try {
      const res = await emisionDTEService.listarDocumentos(rutEmpresa, {
        ambiente, tipoDte: tipoDte || undefined, periodo: periodo || undefined,
      });
      setDocs(res);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [rutEmpresa, ambiente, tipoDte, periodo]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleEstadoEnvio = async (dteId) => {
    setRefreshingId(dteId); setRefreshingType('envio'); setError(null); setInfo(null);
    try {
      const res = await emisionDTEService.consultarEstadoEnvio(dteId);
      setEnvioResultById(prev => ({ ...prev, [dteId]: res }));
      setExpandedId(dteId);
      setDocs(prev => prev.map(d =>
        d.id === dteId ? {
          ...d,
          fecha_consulta_estado: new Date().toISOString(),
          ...(res.estado_sii ? { estado_sii: res.estado_sii } : {}),
          ...(res.glosa ? { glosa_sii: res.glosa } : {}),
        } : d
      ));
    } catch (e) { setError(`Error consultando estado envío: ${e.message}`); }
    finally { setRefreshingId(null); setRefreshingType(null); }
  };

  const handleEstadoDte = async (dteId) => {
    setRefreshingId(dteId); setRefreshingType('dte'); setError(null); setInfo(null);
    try {
      const res = await emisionDTEService.consultarEstadoDte(dteId);
      if (res.estado === 'DNK') {
        const msg = res.err_code === '1'
          ? `Folio ${res.folio}: SII tiene el documento registrado pero los parámetros de consulta (fecha o monto) no coinciden exactamente con el original. El estado previo se mantiene.`
          : `Folio ${res.folio}: SII no encontró el documento (DNK). El estado anterior se mantiene.`;
        setInfo(msg);
        setDocs(prev => prev.map(d =>
          d.id === dteId ? { ...d, fecha_consulta_estado: new Date().toISOString() } : d
        ));
      } else {
        setDocs(prev => prev.map(d =>
          d.id === dteId
            ? {
                ...d,
                estado_sii: res.estado || d.estado_sii,
                glosa_sii: res.glosa || d.glosa_sii,
                fecha_consulta_estado: new Date().toISOString(),
              }
            : d
        ));
      }
    } catch (e) { setError(`Error consultando estado DTE: ${e.message}`); }
    finally { setRefreshingId(null); setRefreshingType(null); }
  };

  const tipoNombre = (codigo) => TIPOS_DTE.find(t => t.codigo === codigo)?.nombre || `T${codigo}`;

  return (
    <Box>
      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ mr: 1 }}>Documentos Emitidos</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Ambiente</InputLabel>
          <Select value={ambiente} label="Ambiente" onChange={e => setAmbiente(e.target.value)}>
            <MenuItem value="certificacion">Certificación</MenuItem>
            <MenuItem value="produccion">Producción</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Tipo DTE</InputLabel>
          <Select value={tipoDte} label="Tipo DTE" onChange={e => setTipoDte(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {TIPOS_DTE.map(t => <MenuItem key={t.codigo} value={t.codigo}>{t.codigo} – {t.nombre}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Período (YYYYMM)" value={periodo}
          onChange={e => setPeriodo(e.target.value)} sx={{ width: 150 }}
          placeholder="202604" />
        <Tooltip title="Recargar">
          <span style={{ display: 'inline-flex' }}>
            <IconButton onClick={cargar} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : <RefreshOutlined />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {info && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {docs.length === 0 && !loading ? (
        <Alert severity="info">No hay documentos emitidos con los filtros seleccionados.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Folio</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Receptor</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="center">Estado SII</TableCell>
              <TableCell>TrackID</TableCell>
              <TableCell>Últ. consulta</TableCell>
              <TableCell align="center">Estado Envío</TableCell>
              <TableCell align="center">Estado DTE</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docs.map(doc => (
              <React.Fragment key={doc.id}>
                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === doc.id ? null : doc.id)}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ExpandMoreOutlined fontSize="small" sx={{
                        transform: expandedId === doc.id ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s', color: 'text.secondary',
                      }} />
                      <strong>{doc.folio}</strong>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={`T${doc.tipo_dte}`} size="small" variant="outlined" color="primary" />
                    <Typography variant="caption" sx={{ ml: 0.5, display: { xs: 'none', md: 'inline' } }}>
                      {tipoNombre(doc.tipo_dte)}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="caption">{doc.fecha_emision}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2">{doc.razon_social_receptor}</Typography>
                    <Typography variant="caption" color="text.secondary">{doc.rut_receptor}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{(doc.monto_total || 0).toLocaleString('es-CL')}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <EstadoChip estado={doc.estado_sii} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{doc.trackid || '–'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {doc.fecha_consulta_estado ? new Date(doc.fecha_consulta_estado).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }) : '–'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" onClick={e => e.stopPropagation()}>
                    <Tooltip title={!doc.trackid ? 'Sin TrackID' : 'Consultar estado del envío (lote) en SII'}>
                      <span style={{ display: 'inline-flex' }}>
                        <IconButton size="small" color="info"
                          onClick={() => handleEstadoEnvio(doc.id)}
                          disabled={refreshingId === doc.id || !doc.trackid}>
                          {refreshingId === doc.id && refreshingType === 'envio'
                            ? <CircularProgress size={16} />
                            : <CloudSyncOutlined fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" onClick={e => e.stopPropagation()}>
                    <Tooltip title={doc.estado_sii === 'GENERADO' ? 'No enviado al SII' : 'Consultar estado del DTE individual en SII'}>
                      <span style={{ display: 'inline-flex' }}>
                        <IconButton size="small" color="primary"
                          onClick={() => handleEstadoDte(doc.id)}
                          disabled={refreshingId === doc.id || doc.estado_sii === 'GENERADO'}>
                          {refreshingId === doc.id && refreshingType === 'dte'
                            ? <CircularProgress size={16} />
                            : <RefreshOutlined fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>

                {/* Fila expandida con detalle */}
                <TableRow>
                  <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expandedId === doc.id} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 4, py: 2, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary" display="block">Emisor</Typography>
                            <Typography variant="body2">{doc.razon_social_emisor} ({doc.rut_emisor})</Typography>
                            {doc.glosa_sii && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">Glosa SII: </Typography>
                                <Typography variant="caption">{doc.glosa_sii}</Typography>
                              </Box>
                            )}
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary" display="block">Montos</Typography>
                            {doc.monto_neto > 0 && <Typography variant="caption" display="block">Neto: {doc.monto_neto?.toLocaleString('es-CL')}</Typography>}
                            {doc.monto_exento > 0 && <Typography variant="caption" display="block">Exento: {doc.monto_exento?.toLocaleString('es-CL')}</Typography>}
                            {doc.iva > 0 && <Typography variant="caption" display="block">IVA ({doc.tasa_iva}%): {doc.iva?.toLocaleString('es-CL')}</Typography>}
                            <Typography variant="body2"><strong>Total: {doc.monto_total?.toLocaleString('es-CL')}</strong></Typography>
                          </Grid>
                          {doc.detalles?.length > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Detalle</Typography>
                              <Table size="small" sx={{ maxWidth: 600 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Nombre</TableCell>
                                    <TableCell align="right">Cant.</TableCell>
                                    <TableCell align="right">Precio</TableCell>
                                    <TableCell align="right">Monto</TableCell>
                                    <TableCell>Exento</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {doc.detalles.map(d => (
                                    <TableRow key={d.nro_linea}>
                                      <TableCell>{d.nro_linea}</TableCell>
                                      <TableCell>{d.nombre}</TableCell>
                                      <TableCell align="right">{d.cantidad}</TableCell>
                                      <TableCell align="right">{d.precio?.toLocaleString('es-CL')}</TableCell>
                                      <TableCell align="right">{d.monto_item?.toLocaleString('es-CL')}</TableCell>
                                      <TableCell>{d.exento ? 'Sí' : ''}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Grid>
                          )}
                          {doc.referencias?.length > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Referencias</Typography>
                              {doc.referencias.map(r => (
                                <Typography key={r.nro_linea} variant="caption" display="block">
                                  {r.tipo_doc} / {r.folio_ref} — {r.razon}
                                </Typography>
                              ))}
                            </Grid>
                          )}
                          {/* Resultado último estado envío */}
                          {envioResultById[doc.id] && (() => {
                            const ev = envioResultById[doc.id];
                            return (
                              <Grid item xs={12}>
                                <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                                  <Typography variant="caption" color="primary" fontWeight="bold" display="block">
                                    Estado Envío (QueryEstUp) — TrackID: {ev.trackid}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    <strong>{ev.estado_envio}</strong> — {ev.glosa_envio}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                                    <Typography variant="caption">Informados: <strong>{ev.informados}</strong></Typography>
                                    <Typography variant="caption" color="success.main">Aceptados: <strong>{ev.aceptados}</strong></Typography>
                                    <Typography variant="caption" color="warning.main">Reparos: <strong>{ev.reparos}</strong></Typography>
                                    <Typography variant="caption" color="error.main">Rechazados: <strong>{ev.rechazados}</strong></Typography>
                                  </Box>
                                  {ev.num_atencion && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                      N° Atención: {ev.num_atencion}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                            );
                          })()}

                          {/* Descargas XML */}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                              {doc.dte_xml ? (
                                <Button size="small" startIcon={<DownloadOutlined />} variant="outlined"
                                  onClick={() => {
                                    const blob = new Blob([doc.dte_xml], { type: 'text/xml' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `DTE_T${doc.tipo_dte}_F${doc.folio}.xml`;
                                    a.click(); URL.revokeObjectURL(url);
                                  }}>
                                  Descargar XML
                                </Button>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  XML no disponible
                                </Typography>
                              )}
                              {doc.envio_xml && (
                                <Tooltip title="XML del sobre de envío al SII (uso técnico)">
                                  <Button size="small" variant="text" color="inherit"
                                    sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                                    onClick={() => {
                                      const blob = new Blob([doc.envio_xml], { type: 'text/xml' });
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `EnvioDTE_T${doc.tipo_dte}_F${doc.folio}.xml`;
                                      a.click(); URL.revokeObjectURL(url);
                                    }}>
                                    EnvioDTE (técnico)
                                  </Button>
                                </Tooltip>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function EmisionDTEPage() {
  const { user } = useAuth();
  const { empresaActiva } = useSelector(state => state.empresa);
  const [tab, setTab] = useState(0);

  if (!user?.is_superuser) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>Acceso restringido</Typography>
        <Typography color="text.secondary">Módulo disponible solo para superadministradores.</Typography>
      </Box>
    );
  }

  const rutEmpresa = empresaActiva ? String(empresaActiva.rut) : '';

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" component="h1">Emisión DTE</Typography>
          <Typography variant="body2" color="text.secondary">
            Emisión directa de documentos tributarios electrónicos al SII — solo superadministradores
          </Typography>
        </Box>
        {empresaActiva ? (
          <Chip
            label={`${empresaActiva.rut}-${empresaActiva.dv} ${empresaActiva.nombre_corto || empresaActiva.razon_social}`}
            color="primary" variant="outlined" size="small"
          />
        ) : (
          <Chip label="Sin empresa seleccionada" color="warning" size="small" />
        )}
      </Box>

      {!empresaActiva && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Seleccione una empresa en el menú superior para usar este módulo.
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tab} onChange={(_, v) => setTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<FolderOutlined />} iconPosition="start" label="Folios / CAF" disabled={!empresaActiva} />
          <Tab icon={<SendOutlined />} iconPosition="start" label="Emitir DTE" disabled={!empresaActiva} />
          <Tab icon={<ListAltOutlined />} iconPosition="start" label="Documentos Emitidos" disabled={!empresaActiva} />
        </Tabs>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <TabPanel value={tab} index={0}><TabFolios rutEmpresa={rutEmpresa} /></TabPanel>
          <TabPanel value={tab} index={1}><TabEmitir rutEmpresa={rutEmpresa} empresaActiva={empresaActiva} /></TabPanel>
          <TabPanel value={tab} index={2}><TabDocumentos rutEmpresa={rutEmpresa} /></TabPanel>
        </Box>
      </Paper>
    </Box>
  );
}
